/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SelectClassOfService.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Reusable component for selecting a Class of Service with search functionality.
    Used in extensions for selecting the class of service profile.

 🔧 Notes:
    - Searchable select component
    - Single selection
    - Filters by class of service name and description

 🔁 Last Updated: 2025-01-15 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface ClassOfService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  id: any; // May be string due to Prisma WASM bug; use uuid when available
  uuid?: string;
  name: string;
  description?: string;
}

interface SelectClassOfServiceProps {
  value: string | number | null; // accepts number or uuid string
  onChange: (value: string | number | null) => void;
  classOfServices?: ClassOfService[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  /** Use 'uuid' to pass cos.uuid to onChange instead of cos.id (bypass Prisma WASM id bug) */
  valueField?: 'id' | 'uuid';
  id?: string;
}

export default function SelectClassOfService({
  value,
  onChange,
  classOfServices = [],
  placeholder = 'Select Class of Service',
  disabled = false,
  className = '',
  error = false,
  valueField = 'id',
  id
}: SelectClassOfServiceProps) {
  const getVal = (cos: ClassOfService) => valueField === 'uuid' ? (cos.uuid ?? cos.id) : cos.id;
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Get selected class of service
  const selectedClassOfService = value != null && value !== 0 && classOfServices
    ? classOfServices.find(cos => getVal(cos) === value)
    : null;

  // Filter class of services based on search
  const filteredClassOfServices = (classOfServices || []).filter(cos =>
    cos.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (cos.description && cos.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (cosId: string | number | null) => {
    onChange(cosId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        id={id}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left border rounded-md
          bg-background text-foreground
          ${error ? 'border-red-500' : 'border-input'}
          hover:bg-accent hover:text-accent-foreground
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-between
          transition-colors
        `}
      >
        <span className={selectedClassOfService ? '' : 'text-muted-foreground'}>
          {selectedClassOfService ? selectedClassOfService.name : placeholder}
        </span>
        <div className="flex items-center gap-1">
          {selectedClassOfService && (
            <span
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer px-1"
            >
              ×
            </span>
          )}
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search class of services..."
              className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto p-1">
            {filteredClassOfServices.length > 0 ? (
              filteredClassOfServices.map((cos) => (
                <button
                  key={cos.id}
                  type="button"
                  onClick={() => handleSelect(getVal(cos))}
                  className={`
                    w-full px-3 py-2 text-left rounded-md
                    hover:bg-accent hover:text-accent-foreground
                    flex items-center justify-between
                    transition-colors
                    ${value === getVal(cos) ? 'bg-accent text-accent-foreground' : ''}
                  `}
                >
                  <div className="flex-1">
                    <div className="font-medium">{cos.name}</div>
                    {cos.description && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {cos.description}
                      </div>
                    )}
                  </div>
                  {value === getVal(cos) && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                No class of services found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
